import styled, { css } from 'styled-components'

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg'

interface StyledButtonProps {
  $variant: ButtonVariant
  $size: ButtonSize
  $fullWidth?: boolean
  $disabled?: boolean
}

export const StyledButton = styled.button<StyledButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[20]};
  
  font-family: ${({ theme }) => theme.typography.body.family};
  font-weight: ${({ theme }) => theme.component.button.common.fontWeight};
  text-decoration: none;
  text-align: center;
  cursor: pointer;
  transition: ${({ theme }) => theme.component.button.common.transition};
  border-radius: ${({ theme }) => theme.component.button.common.borderRadius};
  
  /* Size variants */
  ${({ $size, theme }) => {
    const sizeConfig = theme?.component?.button?.size?.[$size]
    if (!sizeConfig) {
      console.error('Missing size config for button size:', $size, 'Theme:', theme)
      return css`
        height: 40px;
        padding: 8px 16px;
        font-size: 14px;
      `
    }
    return css`
      height: ${sizeConfig.height};
      padding: ${sizeConfig.padding};
      font-size: ${sizeConfig.fontSize};
    `
  }}
  
  /* Color variants */
  ${({ $variant, theme }) => {
    const variantConfig = theme?.component?.button?.[$variant]
    if (!variantConfig) {
      console.error('Missing variant config for button variant:', $variant, 'Theme:', theme)
      return css`
        background-color: #234FEB;
        color: white;
        border: none;
      `
    }
    return css`
      background-color: ${variantConfig.backgroundColor};
      color: ${variantConfig.color};
      border: ${variantConfig.border};
      text-decoration: none;
      
      &:not(:disabled):hover {
        background-color: ${variantConfig.hover.backgroundColor};
        color: ${variantConfig.hover.color};
        border: ${variantConfig.border};
        text-decoration: none;
      }
      
      &:not(:disabled):active {
        background-color: ${variantConfig.active.backgroundColor};
        color: ${variantConfig.active.color};
        border: ${variantConfig.border};
        text-decoration: none;
      }
    `
  }}
  
  /* Full width */
  ${({ $fullWidth }) => $fullWidth && css`
    width: 100%;
  `}
  
  /* Disabled state */
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    background-color: ${({ theme }) => theme.palette.disabled.default};
    color: ${({ theme }) => theme.palette.disabled.emphasis};
    border: ${({ theme }) => `${theme.border.width.sm} solid ${theme.palette.disabled.default}`};
  }

  &:focus {
    outline: ${({ theme }) => theme.component.interaction.focus.outline};
    outline-offset: ${({ theme }) => theme.component.interaction.focus.outlineOffset};
  }
`